import { ContentType } from "@brookbush/database";
import { StructuredDataService } from "@brookbush/structured-data";
import type { CertificationPreview } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import type { GetStaticPaths, GetStaticProps } from "next";
import { useRouter } from "next/compat/router";
import { NextSeo } from "next-seo";
import { useEffect } from "react";
import { mutate } from "swr";

import CertificationOutOfScopeOverlay from "./components/CertificationOutOfScopeOverlay";
import CertificationOverview from "./components/CertificationOverview";
import CertificationSidebar from "./components/CertificationSidebar";
import CertificationStudyPlan from "./components/CertificationStudyPlan";
import CertificationTutorial from "./components/CertificationTutorial";
import { useCertification, useRecommendedCoursePlan } from "~/hooks";
import useChatPageContent from "~/hooks/useChatPageContent";
import { GridLayout } from "~/layouts";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

const CertificationPage: NextPageWithProps<{
	structuredData: string;
}> = ({ structuredData }) => {
	const { certification, isLoading } = useCertification();

	const isStudyPlan = Boolean(certification?.isStudyPlan);

	const { recommendedCoursePlan } = useRecommendedCoursePlan(
		isStudyPlan ? certification.slug : undefined,
	);

	useChatPageContent({
		contentType: ContentType.CERTIFICATION,
		contentId: certification?._id ?? "",
		title: certification?.title ?? "",
	});

	const { userId } = useAuth();

	const router = useRouter();

	useEffect(() => {
		if (userId) {
			mutate(`/certifications`);
			mutate(`/certifications/${router?.query.certificationSlug}`);
			mutate(
				`/certifications/${router?.query.certificationSlug}/modules?courseLimit=6`,
			);
		}
	}, [router?.query.certificationSlug, userId]);

	useEffect(() => {
		if (certification.isNotAllowed && certification.isLegacy) {
			router?.replace(
				`/certifications/${certification.slug.replace("-original", "")}`,
			);
		}
	}, [
		certification.isLegacy,
		certification.isNotAllowed,
		certification.slug,
		isLoading,
		router,
	]);

	return (
		<>
			<NextSeo
				title={certification.title}
				description={certification.description}
				openGraph={{
					images: [
						{
							url: certification.thumbnail.url,
							alt: certification.thumbnail.alt,
						},
					],
				}}
			/>

			<script
				type="application/ld+json"
				id="json-ld"
				dangerouslySetInnerHTML={{
					__html: structuredData,
				}}
			/>

			<CertificationTutorial />

			<CertificationOutOfScopeOverlay />

			<GridLayout.Sidebar
				className={isStudyPlan ? "hidden xl:block" : "z-20 laptop:block hidden"}
			>
				<CertificationSidebar />
			</GridLayout.Sidebar>

			<GridLayout.Body>
				{isStudyPlan ? (
					<CertificationStudyPlan coursePlan={recommendedCoursePlan} />
				) : (
					<CertificationOverview />
				)}
			</GridLayout.Body>
		</>
	);
};

CertificationPage.Layout = GridLayout;

export const getStaticPaths: GetStaticPaths = async () => {
	const certifications = await fetcher("/certifications");

	return {
		paths: certifications
			.filter((c: CertificationPreview) => c.slug !== "courses")
			.map((c: CertificationPreview) => `/certifications/${c.slug}`),
		fallback: "blocking",
	};
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
	try {
		const [certification, modules, certifications, recommendedCoursePlan] =
			await Promise.all([
				fetcher(`/certifications/${params!.certificationSlug}`),
				fetcher(
					`/certifications/${params!.certificationSlug}/modules?courseLimit=6`,
				),
				fetcher(`/certifications`),
				fetcher(
					`/certifications/${params!.certificationSlug}/recommended-course-plan`,
				),
			]);

		const structuredData = await Promise.all([
			StructuredDataService.generateCertification(certification, modules),
			StructuredDataService.getBreadcrumbs(
				`/certifications/${certification.slug}`,
				(fragment) =>
					fragment === certification.slug ? certification.title : undefined,
			),
		]);

		for (const result of structuredData) {
			if (!result) {
				return {
					notFound: true,
				};
			}
		}

		return {
			props: {
				fallback: {
					[`/certifications/${params!.certificationSlug}`]: certification,
					[`/certifications/${
						params!.certificationSlug
					}/modules?courseLimit=6`]: modules,
					[`/certifications`]: certifications,
					[`/certifications/${
						params!.certificationSlug
					}/recommended-course-plan`]: recommendedCoursePlan,
				},
				structuredData: StructuredDataService.withContext({
					"@graph": structuredData,
				}),
			},
		};
	} catch (e) {
		console.log(e);

		return {
			notFound: true,
		};
	}
};

export default CertificationPage;
